/**
 * THE DRAFT ACCOUNTS (ADR-0019 F) — what a preparation's current version
 * says, set the way a set of draft accounts is set.
 *
 * EVERY FIGURE IS THE SERVER'S. The engine computed every total; the work OS
 * formatted every amount and wrote every sentence (`work.preparation`). This
 * file arranges them and computes nothing: no sum, no sign, no balance check.
 *
 * THE ORDER IS THE READER'S. The answer and its figures first; then what is
 * left for the accountant to decide, with the one act that applies those
 * decisions ("Prepare again"); then the accounts themselves; and the working
 * — trial balance, cash book, mappings — one click away for whoever checks it.
 */
import { useState } from "react";
import { WorkActionResponse, type PreparationView, type WorkFile } from "@leveri/contracts";
import { Button, Figure, Label, Notice, RuledList, Section, SectionHead, StateText, Statement, markerTone } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";

const FIGURE_STRIP = "mt-6 grid grid-cols-1 border-y border-rule sm:grid-cols-2 lg:grid-cols-4";
const SUMMARY = "cursor-pointer list-none text-[15px] font-semibold text-ink hover:underline";
const NUM = "text-right [font-variant-numeric:tabular-nums]";

/** 1 — the answer, the figures it is about, and the basis it was prepared on. */
export function PreparationLead({ preparation }: { preparation: PreparationView }) {
  return (
    <Section id="figures">
      <Statement>{preparation.headline}</Statement>
      <p className="mt-3 text-[13px] text-stone">
        <StateText tone={markerTone(preparation.tone)}>{preparation.verdictLabel}</StateText>
        {" · "}{preparation.periodicityLabel} · {preparation.basisLabel}{preparation.periodText ? ` · ${preparation.periodText}` : ""}
      </p>
      {preparation.figures.length > 0 && (
        <div className={FIGURE_STRIP}>
          {preparation.figures.map((figure, index) => (
            <div key={figure.key} className={`py-4 ${index > 0 ? "lg:border-l lg:border-rule-soft lg:pl-6" : ""}`}>
              <div className="text-[12px] text-stone">{figure.label}</div>
              <div className="mt-1.5 font-mono text-[19px] text-ink [font-variant-numeric:tabular-nums]">{figure.text}</div>
              {figure.note && <p className="mt-1 text-[12px] text-stone">{figure.note}</p>}
            </div>
          ))}
        </div>
      )}
      {preparation.memorySentence && <p className="mt-3 text-[13px] text-stone">{preparation.memorySentence}</p>}
    </Section>
  );
}

/**
 * 2 — what only the accountant can settle, and PREPARE AGAIN. The button is
 * offered only when the server says the work can be prepared again (settled
 * work, review authority, at least one decision on this version); otherwise
 * the server's sentence says why the decisions are not applied yet.
 */
export function PreparationDecide({ work, preparation, onWork, onOutcome }: {
  work: WorkFile;
  preparation: PreparationView;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const again = preparation.prepareAgain;
  if (preparation.reviewerItems.length === 0 && !again.sentence) return null;

  async function prepareAgain() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${work.id}/prepare-again`, WorkActionResponse);
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section id="decide">
      {preparation.reviewerItems.length > 0 && (
        <>
          <SectionHead>For you to decide</SectionHead>
          <RuledList>
            {preparation.reviewerItems.map((item, index) => (
              <p key={`${index}-${item.slice(0, 24)}`} className="py-3 text-[15px] text-ink">{item}</p>
            ))}
          </RuledList>
          <p className="mt-2 text-[13px] text-stone">
            Decide each one in your review below: accept Leveri&rsquo;s class, or adjust it to the right one. Your decisions are remembered for this client.
          </p>
        </>
      )}
      {again.sentence && (
        <div className={preparation.reviewerItems.length > 0 ? "mt-5" : ""}>
          <p className="text-sm text-ink" data-testid="prepare-again-sentence">{again.sentence}</p>
          {again.available && (
            <div className="mt-3">
              <Button disabled={busy} onClick={() => void prepareAgain()} data-testid="prepare-again">
                {busy ? "Preparing again…" : "Prepare again with my decisions"}
              </Button>
            </div>
          )}
        </div>
      )}
      {failure && <Notice className="mt-3">{failure}</Notice>}
    </Section>
  );
}

function Block({ block, first }: { block: PreparationView["profitAndLoss"]["blocks"][number]; first: boolean }) {
  return (
    <div className={first ? "" : "mt-5"}>
      <Label className="mb-1">{block.label}</Label>
      {block.lines.map((line, index) => (
        <Figure key={`${line.label}-${index}`} first={index === 0} label={line.label} value={line.text} />
      ))}
      <Figure label={`Total ${block.label.toLowerCase()}`} value={block.totalText} total />
    </div>
  );
}

/** 3 — the accounts, and the working behind them. */
export function PreparationBooks({ preparation }: { preparation: PreparationView }) {
  const pl = preparation.profitAndLoss;
  const bs = preparation.balanceSheet;
  return (
    <>
      <Section id="profit-and-loss">
        <SectionHead>Profit and loss</SectionHead>
        {pl.blocks.length === 0 ? (
          <p className="text-sm text-stone">Nothing has been classified to income or expenses yet.</p>
        ) : (
          pl.blocks.map((block, index) => <Block key={block.key} block={block} first={index === 0} />)
        )}
        <Figure className="mt-5" label={pl.netProfitMinorUnits >= 0 ? "Profit for the period" : "Loss for the period"} value={pl.netProfitText} total />
      </Section>

      <Section id="balance-sheet">
        <SectionHead>Balance sheet</SectionHead>
        {bs.blocks.map((block, index) => <Block key={block.key} block={block} first={index === 0} />)}
        <Figure className="mt-5" label="Assets" value={bs.assetsText} total />
        <Figure label="Liabilities and equity" value={bs.fundingText} total />
        {!bs.balanced && (
          <p className="mt-3 text-[13px]"><StateText tone={markerTone("danger")}>The draft balance sheet does not balance. It cannot be relied on until the difference is found.</StateText></p>
        )}
      </Section>

      <Section id="working">
        <SectionHead>The working</SectionHead>
        <div className="flex flex-col gap-5">
          <details>
            <summary className={SUMMARY}>
              Draft trial balance{" "}
              <span className="ml-1 text-[13px] font-normal text-stone">
                {preparation.trialBalanceTotals.balanced ? "balances" : "does not balance"} · {preparation.trialBalanceTotals.debitText} debit · {preparation.trialBalanceTotals.creditText} credit
              </span>
            </summary>
            {/* Wide on purpose: six columns. It scrolls inside its own box on a phone, never the page. */}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13px]">
                <thead>
                  <tr className="border-b border-rule text-left text-stone">
                    <th className="py-2 pr-3 font-normal">Account</th>
                    <th className="py-2 pr-3 font-normal">Group</th>
                    <th className={`py-2 pr-3 font-normal ${NUM}`}>Opening</th>
                    <th className={`py-2 pr-3 font-normal ${NUM}`}>Debit</th>
                    <th className={`py-2 pr-3 font-normal ${NUM}`}>Credit</th>
                    <th className={`py-2 font-normal ${NUM}`}>Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {preparation.trialBalance.map((row) => (
                    <tr key={row.account} className="border-b border-rule-soft text-ink">
                      <td className="py-2 pr-3">{row.label}</td>
                      <td className="py-2 pr-3 text-stone">{row.groupLabel}</td>
                      <td className={`py-2 pr-3 ${NUM}`}>{row.openingText}</td>
                      <td className={`py-2 pr-3 ${NUM}`}>{row.debitText}</td>
                      <td className={`py-2 pr-3 ${NUM}`}>{row.creditText}</td>
                      <td className={`py-2 ${NUM}`}>{row.closingText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <details>
            <summary className={SUMMARY}>
              Cash book by class <span className="ml-1 text-[13px] font-normal text-stone">{preparation.cashBook.length}</span>
            </summary>
            <div className="mt-3">
              {preparation.cashBook.map((row, index) => (
                <Figure
                  key={`${row.accountLabel}-${row.classLabel}-${index}`}
                  first={index === 0}
                  label={row.classLabel}
                  citation={`${row.accountLabel} · ${row.lines} line${row.lines === 1 ? "" : "s"}`}
                  value={row.totalText}
                />
              ))}
            </div>
          </details>

          {preparation.mappings.length > 0 && (
            <details>
              <summary className={SUMMARY}>
                Last period&rsquo;s accounts, mapped <span className="ml-1 text-[13px] font-normal text-stone">{preparation.mappings.length}</span>
              </summary>
              <RuledList className="mt-3">
                {preparation.mappings.map((m) => (
                  <div key={m.account} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-2.5 text-[15px]">
                    <span className="text-ink">{m.label}</span>
                    <span className="text-[13px] text-stone">{m.classLabel ?? "No class yet"} · {m.mappingLabel}</span>
                  </div>
                ))}
              </RuledList>
            </details>
          )}

          {preparation.notes.length > 0 && (
            <details>
              <summary className={SUMMARY}>
                Notes on the draft <span className="ml-1 text-[13px] font-normal text-stone">{preparation.notes.length}</span>
              </summary>
              <RuledList className="mt-3">
                {preparation.notes.map((note, index) => <p key={`${index}-${note.slice(0, 24)}`} className="py-2.5 text-sm text-ink-soft">{note}</p>)}
              </RuledList>
            </details>
          )}
        </div>
      </Section>
    </>
  );
}
